import { Flex, Skeleton, Card, CardBody, Box, Spacer } from '@chakra-ui/react';
import React, { useState, useEffect } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  useGetLimitedPartnerCapitalTrendDataQuery,
  LimitedPartnerCapitalTrendDataGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { Typography } from 'packages/common-frontend/components/Typography';
import { colors } from '~/packages/common-frontend/colors';
import { getDefaultDashboardDisplayEndDate } from '~/packages/common-frontend/constants';
import { PortfolioChartCards } from '~/packages/portfolio/ui/shared/PortfolioChartCards';
import {
  ChartTimePeriod,
  PortfolioChartConfig,
  getInitialActiveFields,
  onToggleActiveField,
} from '~/packages/portfolio/ui/shared/portfolioChartUtils';
import { PortfolioReChart } from '~/packages/portfolio/ui/shared/PortfolioReChart';

type LimitedPartnerCapitalTrendData = {
  reportingDate: Util.FullDateStr;
  committedCapital: number;
  uncalledCapital: number;
};

export const capitalTrendsChartConfig: PortfolioChartConfig<
  keyof LimitedPartnerCapitalTrendData
> = {
  committedCapital: {
    chartType: 'dashedLine',
    field: 'committedCapital',
    label: 'Committed Capital',
    format: 'currency',
    color: colors.gray[950],
  },
  uncalledCapital: {
    chartType: 'line',
    field: 'uncalledCapital',
    label: 'Uncalled Capital',
    format: 'currency',
    color: colors.chart.sky,
  },
};

const CustomLegend = () => {
  return (
    <Flex mb="2rem" gap="1rem">
      <Flex alignItems="center" gap="0.5rem">
        <Box
          backgroundColor={colors.gray[950]}
          borderRadius="4px"
          h="8px"
          w="8px"
        />
        <Typography variant="detail-muted">Committed Capital</Typography>
      </Flex>
      <Flex alignItems="center" gap="0.5rem">
        <Box
          backgroundColor={colors.chart.sky}
          borderRadius="4px"
          h="8px"
          w="8px"
        />
        <Typography variant="detail-muted">Uncalled Capital</Typography>
      </Flex>
    </Flex>
  );
};

const getMonthYear = (date: Util.FullDateStr) => {
  const month = commonDate.fullDateStrToDate(date).getMonth();
  const year = commonDate.fullDateStrToDate(date).getFullYear();
  return `${month}-${year}`;
};

// trend data is sorted by finleyReceivableId and then by reportingDate by backend
// function calculates the total committed and uncalled capital for limited partner
// as of the end of each month
export const compileCapitalTrendData = (
  capitalTrendData: LimitedPartnerCapitalTrendDataGQLType[],
) => {
  const capitalTrendDataMap: {
    [monthYear: string]: LimitedPartnerCapitalTrendData;
  } = {};
  capitalTrendData.forEach((row, index, arr) => {
    const current = row;
    const next = arr[index + 1];
    if (
      !next ||
      current.finleyReceivableId !== next.finleyReceivableId ||
      getMonthYear(current.reportingDate) !== getMonthYear(next.reportingDate)
    ) {
      const currentMonthYear = getMonthYear(current.reportingDate);
      if (!capitalTrendDataMap[currentMonthYear]) {
        capitalTrendDataMap[currentMonthYear] = {
          reportingDate: current.reportingDate,
          committedCapital: 0,
          uncalledCapital: 0,
        };
      }
      capitalTrendDataMap[currentMonthYear].committedCapital +=
        current.committedCapital;
      capitalTrendDataMap[currentMonthYear].uncalledCapital +=
        current.uncalledCapital;
    }
  });
  return Object.values(capitalTrendDataMap).sort((a, b) => {
    return (
      commonDate.fullDateStrToDate(a.reportingDate).getTime() -
      commonDate.fullDateStrToDate(b.reportingDate).getTime()
    );
  });
};

export const LimitedPartnerCapitalTrendsGraphContainer = ({
  limitedPartnerId,
}: {
  limitedPartnerId: string;
}) => {
  const {
    data: limitedPartnerCapitalTrendData,
    error: limitedPartnerCapitalTrendDataError,
  } = useGetLimitedPartnerCapitalTrendDataQuery({
    variables: {
      limitedPartnerId,
    },
  });
  if (limitedPartnerCapitalTrendDataError) {
    throw limitedPartnerCapitalTrendDataError;
  }

  const initialActiveFields = getInitialActiveFields({
    chartConfig: capitalTrendsChartConfig,
  }) as (keyof LimitedPartnerCapitalTrendData)[];

  const [activeFields, setActiveFields] = useState<
    (keyof LimitedPartnerCapitalTrendData)[]
  >(initialActiveFields);

  const [
    selectedBar,
    setSelectedBar,
  ] = useState<LimitedPartnerCapitalTrendData>();

  const [capitalTrendGraphData, setCapitalTrendGraphData] = useState<
    LimitedPartnerCapitalTrendData[]
  >();

  useEffect(() => {
    if (limitedPartnerCapitalTrendData) {
      const compiledTrendData = compileCapitalTrendData(
        limitedPartnerCapitalTrendData.capitalTrendData,
      );
      setCapitalTrendGraphData(compiledTrendData);
      setSelectedBar(compiledTrendData[compiledTrendData.length - 1]);
    }
  }, [limitedPartnerCapitalTrendData]);

  return (
    <Flex direction="column" gap="1rem" mt="2.5rem">
      <Typography variant="section-subtitle" trim>
        Committed and Uncalled Capital Trends
      </Typography>
      {capitalTrendGraphData ? (
        <Card variant="outline" size="lg">
          <CardBody display="flex">
            <Box width="70%">
              <PortfolioReChart
                chartData={capitalTrendGraphData}
                selectedTimePeriod={ChartTimePeriod.month}
                currentSelectedBarReportingDate={
                  selectedBar
                    ? selectedBar.reportingDate
                    : getDefaultDashboardDisplayEndDate()
                }
                onSelectBar={setSelectedBar}
                chartConfig={capitalTrendsChartConfig}
                visibleChartComponents={activeFields}
                chartMargin={{ top: 0, right: 20, left: 10, bottom: 0 }}
                legendAlign={{ horizontalAlign: 'left', verticalAlign: 'top' }}
                renderCustomLegend={CustomLegend}
                legendWrapperStyle={{}}
                tooltipLabelType="monthYear"
              />
            </Box>
            <Spacer />
            {selectedBar && (
              <Flex width="25%" flexDirection="column">
                <PortfolioChartCards
                  selectedBarData={selectedBar}
                  chartConfig={capitalTrendsChartConfig}
                  activeFields={activeFields}
                  onToggleActiveField={(selectedField) =>
                    onToggleActiveField({
                      selectedField,
                      currentActiveFields: activeFields,
                      setActiveFields,
                    })
                  }
                  customTitleFormatter={(selectedBarData) =>
                    `${commonDate.fullDateStrToStr(
                      selectedBarData.reportingDate,
                      'LLLL',
                    )} ${commonDate.fullDateStrToStr(
                      selectedBarData.reportingDate,
                      'yyyy',
                    )}`
                  }
                />
              </Flex>
            )}
          </CardBody>
        </Card>
      ) : (
        <Skeleton height="60vh" />
      )}
    </Flex>
  );
};
